import { useNavigate, useLocation } from 'react-router-dom';
import { X, Minus, Plus, ShoppingBag, Trash2 } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../pages/customer/templates/shared';
import { t } from '../i18n'

// Slide-in cart sidebar for the ecommerce storefront - the Shopify-style
// "added to cart" experience, instead of navigating away to a separate page.
export default function CartDrawer() {
  const { items, total, itemCount, isDrawerOpen, closeDrawer, updateQuantity, removeItem } = useCart();
  const currency = useCurrency();
  const { search } = useLocation();
  const navigate = useNavigate();

  if (!isDrawerOpen) return null;

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label={t("Shopping cart")}>
      <div className="absolute inset-0 bg-black/40" onClick={closeDrawer} />
      <div className="absolute top-0 end-0 h-full w-full max-w-md cart-drawer shadow-2xl flex flex-col animate-slideInRight">
        <div className="flex items-center justify-between px-5 py-4 border-b sk-divider">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" /> {t("Your cart (")}{itemCount})
          </h2>
          <button onClick={closeDrawer} className="sk-muted p-1" aria-label={t("Close cart")}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center sk-muted">
              <ShoppingBag className="w-12 h-12 mb-3" />
              <p>{t("Your cart is empty")}</p>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map(item => (
                <li key={item.id} className="flex gap-3">
                  <div className="w-16 h-16 rounded-lg overflow-hidden sk-soft flex-shrink-0">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center sk-muted"><ShoppingBag className="w-6 h-6" /></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <p className="font-medium text-sm truncate">{item.name}</p>
                      <button onClick={() => removeItem(item.id)} className="sk-muted hover:text-red-500 flex-shrink-0" aria-label={t("Remove item")}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {item.variantName && <p className="text-xs sk-muted">{item.variantName}</p>}
                    <div className="flex items-center justify-between mt-2">
                      <div className="sk-qty">
                        <button onClick={() => updateQuantity(item.id, item.quantity - 1)} aria-label={t("Decrease quantity")}>
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="text-sm">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.id, item.quantity + 1)} aria-label={t("Increase quantity")}>
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <span className="text-sm font-semibold">{item.total.toFixed(2)} {currency}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t sk-divider px-5 py-4 space-y-3">
            <div className="flex justify-between text-base font-semibold">
              <span>{t("Subtotal")}</span>
              <span>{total.toFixed(2)} {currency}</span>
            </div>
            <button
              onClick={() => { closeDrawer(); navigate(`/checkout${search}`); }}
              className="sk-btn w-full"
            >
              {t("Checkout")}
            </button>
            <button onClick={closeDrawer} className="w-full text-center text-sm sk-muted underline">
              {t("Continue shopping")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
